import type { GroupByOperation } from '../OperationTypes.js'
import { NoConfig } from '../OperationTypes.js'
import { type SampleBucket } from '../OperationTypes.js'
import type { MetadataFieldDescriptor, MetadataFields } from '../MetadataFields.js'
import { defaultKey } from '../OperationTypes.js'
import type { VisualizationSampleContainer } from '../VisualizationSampleContainer.js'
import { compareGroupValues } from './GroupValueOrder.js'

const createGroupByOperation = <T extends string, TConfig>(op: GroupByOperation<T, TConfig>) => op

export const groupByValue = createGroupByOperation({
  key: 'group-by-value',
  type: 'groupBy',
  label: 'Group by value',
  applicableTo: ['string', 'enum', 'datetime', 'datetime:duration'],

  apply: (buckets, field, _config, projection) => {
    const result: SampleBucket[] = []

    for (const bucket of buckets) {
      const grouping = new Map<string, { samples: VisualizationSampleContainer[]; label?: string }>()

      for (const sample of bucket.samples) {
        /*
         * A projection is the field's own bucketing rule — `project-month-year` and friends floor a
         * timestamp and name the result. Its `value` identifies the group and its `label` names it; the
         * label used to be discarded, so a month grouping was titled with the raw stored timestamp.
         *
         * Without one, `comparisonKey` is the field's answer to "what makes two samples the same
         * group", which every other consumer of these descriptors already honours.
         */
        const projected = projection?.(sample)
        const groupValue = projected
          ? projected.value
          : String((field.comparisonKey ? field.comparisonKey(sample) : field.getter(sample)) ?? 'undefined')

        if (!grouping.has(groupValue)) grouping.set(groupValue, { samples: [], label: projected?.label })
        grouping.get(groupValue)!.samples.push(sample)
      }

      /*
       * Buckets used to come out in Map insertion order, i.e. whatever order the samples happened to
       * arrive in — so a date grouping produced an unordered axis (S2-815, S2-648). Sorted per outer
       * bucket rather than over the whole result, so an enclosing grouping keeps its own order.
       */
      const ordered = [...grouping.entries()].sort(([a], [b]) => compareGroupValues(a, b, field.type))

      for (const [groupValue, group] of ordered) {
        result.push({
          ...computeKeyAndLabel(bucket, field, groupValue, group.samples, group.label),
          samples: group.samples
        })
      }
    }

    return result
  },
  stringify: (fields: MetadataFields, bound) => {
    const field = fields[bound.metaDataField]
    return field ? `Group ${field.label}` : 'Group'
  },
  config: NoConfig
})

const computeKeyAndLabel = (
  prevBucket: SampleBucket,
  field: MetadataFieldDescriptor,
  value: string,
  samples: VisualizationSampleContainer[],
  projectedLabel?: string
) => {
  /*
   * A projection has already named the group, so its label wins — otherwise a month grouping would be
   * titled by whichever sample happened to come first. `uiLabel` is next: without it a datetime field
   * falls back to the raw stored value, which is the full ISO timestamp including its offset.
   */
  const displayValue = projectedLabel ?? (field.uiLabel && samples.length > 0 ? field.uiLabel(samples[0]) : value)

  const labelBase = `${displayValue}`
  const keyBase = `${field.key}[${value}]`

  if (prevBucket.key === defaultKey) {
    return {
      key: keyBase,
      label: labelBase + ` [${samples.length}]`
    }
  }
  return {
    key: `${prevBucket.key}::${keyBase}`,
    label: `${prevBucket.label}, ${labelBase} [${samples.length}]`
  }
}
